import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, Phone, Languages, AudioLines, Fingerprint, Radar, Target } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { IMAGES } from "@/config";

const STATS = [
  ["07", "AI capabilities"],
  ["20+", "languages supported"],
  ["24×7", "service availability"],
  ["03", "connected layers"],
];

const LAYERS = [
  {
    num: "01",
    title: "Interact",
    italic: "Interact",
    desc: "Handle and understand the conversation — voice, text, and every language your customers speak.",
    products: ["AI Voice Agent", "Live Transcription & Translation"],
    icon: Phone,
    img: IMAGES.wave,
  },
  {
    num: "02",
    title: "Understand",
    italic: "Understand",
    desc: "Turn every conversation into intelligence — tone, intent, summaries, and a profile that reasons.",
    products: ["Sentiment & Conversation Intelligence", "AI Digital Twin"],
    icon: Fingerprint,
    img: IMAGES.glass,
  },
  {
    num: "03",
    title: "Protect Value",
    italic: "Value",
    desc: "Act across the customer lifecycle — predict churn, personalise retention, win back what's lost.",
    products: ["ChurnSense AI", "RetainAI", "WinBack AI"],
    icon: Radar,
    img: IMAGES.approach,
  },
];

const MANIFESTO = [
  {
    num: "01",
    title: "Every conversation is an asset",
    body: "An AI voice agent answers in 20+ languages, around the clock, while live transcription turns speech into structured, machine-readable truth. Nothing said is ever lost.",
    icon: AudioLines,
  },
  {
    num: "02",
    title: "Intelligence over recording",
    body: "Sentiment, intent, summaries and next steps surface in real time. A digital twin unifies every touchpoint into one profile that reasons about why customers behave the way they do.",
    icon: Languages,
  },
  {
    num: "03",
    title: "Revenue defended, not reported",
    body: "Churn is predicted before it happens, retention offers are sized to the minimum effective incentive, and lost customers are won back with precision — not blanket discounts.",
    icon: Target,
  },
];

export default function Home() {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 260]);
  const fade = useTransform(scrollY, [0, 600], [1, 0.15]);
  const bandRef = useRef(null);
  const { scrollYProgress: bandProgress } = useScroll({ target: bandRef, offset: ["start end", "end start"] });
  const bandY = useTransform(bandProgress, [0, 1], ["-12%", "12%"]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex flex-col justify-end overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
          <img src={IMAGES.hero} alt="" className="w-full h-[120%] object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/40 to-void" />
        </motion.div>

        <div className="max-w-[1440px] mx-auto px-6 md:px-12 w-full pt-40 pb-16 md:pb-24">
          <motion.p
            data-testid="hero-overline"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="font-mono-tech text-xs uppercase tracking-[0.25em] text-vermilion mb-8"
          >
            MINDENGINE Technologies · An Agentic AI Enterprise · Global
          </motion.p>

          <h1 data-testid="hero-headline" className="font-display text-5xl md:text-7xl lg:text-[6.5rem] tracking-tighter leading-[0.95] text-bone max-w-6xl">
            <MaskedLine delay={0.35}>Seven AI products.</MaskedLine>
            <MaskedLine delay={0.5}>
              One <span className="italic text-vermilion">connected</span> layer
            </MaskedLine>
            <MaskedLine delay={0.65}>for the entire customer</MaskedLine>
            <MaskedLine delay={0.8}>
              <span className="italic">lifecycle.</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 max-w-xl text-ash text-base md:text-lg leading-relaxed"
          >
            A portfolio spanning customer interaction, agent assistance, customer intelligence and lifecycle
            value protection — built for enterprises that refuse to lose customers.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-demo"
              className="group flex items-center gap-3 bg-vermilion text-white font-mono-tech text-xs uppercase tracking-[0.2em] px-8 py-5 transition-colors duration-300 hover:bg-vermilion-dark active:scale-[0.98]"
            >
              Request a demo
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/capabilities"
              data-testid="hero-cta-capabilities"
              className="group flex items-center gap-3 font-mono-tech text-xs uppercase tracking-[0.2em] text-bone border-b border-line pb-2 transition-colors duration-300 hover:text-vermilion hover:border-vermilion"
            >
              Explore the seven products
              <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-1" />
            </Link>
          </motion.div>
        </div>

        <motion.div style={{ opacity: fade }} className="border-t border-line bg-void/40 backdrop-blur-sm">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4">
            {STATS.map(([num, label], i) => (
              <motion.div
                key={label}
                data-testid={`hero-stat-${i}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5 + i * 0.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="py-8 md:py-10 pr-6 border-r border-line last:border-r-0 md:[&:nth-child(2)]:border-r"
              >
                <p className="font-mono-tech text-3xl md:text-4xl text-bone">{num}</p>
                <p className="mt-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-ash">{label}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      <Marquee />

      {/* THREE LAYERS — BENTO */}
      <section data-testid="home-layers-section" className="py-32 md:py-48">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-vermilion">The Portfolio</p>
            <h2 className="mt-6 font-display text-4xl md:text-6xl tracking-tight text-bone max-w-3xl leading-[1.05]">
              Three layers. <span className="italic">One</span> operating system for customer value.
            </h2>
          </Reveal>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-line border border-line">
            {LAYERS.map((layer, i) => (
              <Reveal key={layer.num} delay={i * 0.12} className="bg-void">
                <Link
                  to="/capabilities"
                  data-testid={`layer-card-${layer.num}`}
                  className="group relative block p-8 md:p-12 h-full min-h-[420px] flex flex-col justify-between overflow-hidden"
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-700">
                    <img src={layer.img} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="relative">
                    <div className="flex items-start justify-between">
                      <span className="font-mono-tech text-sm text-vermilion">{layer.num}</span>
                      <layer.icon size={22} strokeWidth={1} className="text-ash group-hover:text-vermilion transition-colors duration-500" />
                    </div>
                    <h3 className="mt-16 font-display text-3xl md:text-4xl text-bone">
                      {layer.title === "Protect Value" ? (
                        <>Protect <span className="italic text-vermilion">Value</span></>
                      ) : (
                        <span className="italic">{layer.italic}</span>
                      )}
                    </h3>
                    <p className="mt-6 text-ash text-sm md:text-base leading-relaxed">{layer.desc}</p>
                  </div>
                  <div className="relative mt-12">
                    <div className="h-px w-full bg-line mb-6" />
                    <ul className="space-y-2">
                      {layer.products.map((p) => (
                        <li key={p} className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-bone/80">
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section data-testid="home-manifesto-section" className="py-32 md:py-48 border-t border-line">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-vermilion">The Manifesto</p>
          </Reveal>
          <div className="mt-16">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.num} delay={i * 0.1}>
                <div
                  data-testid={`manifesto-chapter-${m.num}`}
                  className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 py-12 md:py-16 border-t border-line items-start"
                >
                  <span className="md:col-span-2 font-mono-tech text-sm text-vermilion pt-2">{m.num}</span>
                  <h3 className="md:col-span-5 font-display text-3xl md:text-5xl tracking-tight text-bone leading-[1.05] transition-colors duration-500 group-hover:text-vermilion">
                    {m.title}
                  </h3>
                  <p className="md:col-span-4 text-ash text-sm md:text-base leading-relaxed">{m.body}</p>
                  <m.icon size={28} strokeWidth={1} className="hidden md:block md:col-span-1 text-ash group-hover:text-vermilion transition-colors duration-500 justify-self-end" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARALLAX IMAGE BAND */}
      <section ref={bandRef} data-testid="home-image-band" className="relative border-t border-line overflow-hidden">
        <div className="relative h-[55vh] md:h-[70vh]">
          <motion.img
            src={IMAGES.tunnel}
            alt="Abstract tunnel of light"
            style={{ y: bandY }}
            className="absolute inset-0 w-full h-[125%] object-cover opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-void via-void/40 to-void/70" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 pb-14 md:pb-20 w-full">
              <Reveal>
                <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-vermilion mb-6">End to end</p>
                <p className="font-display text-3xl md:text-5xl tracking-tight text-bone max-w-2xl leading-[1.05]">
                  The customer lifecycle, <span className="italic">defended</span> at every step.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="home-cta-section" className="relative py-32 md:py-48 border-t border-line overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img src={IMAGES.glass} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-void via-void/60 to-void" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-display text-4xl md:text-7xl tracking-tighter text-bone max-w-4xl leading-[0.98]">
              Your customers are deciding <span className="italic text-vermilion">right now.</span>
            </h2>
            <p className="mt-8 max-w-lg text-ash text-base md:text-lg leading-relaxed">
              See all seven capabilities working as one layer. Thirty minutes, your use case, live product.
            </p>
            <div className="mt-12 flex flex-wrap gap-6 items-center">
              <Link
                to="/contact"
                data-testid="home-cta-button"
                className="group flex items-center gap-3 bg-vermilion text-white font-mono-tech text-xs uppercase tracking-[0.2em] px-8 py-5 transition-colors duration-300 hover:bg-vermilion-dark active:scale-[0.98]"
              >
                Request a demo
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
